import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const SEARCH_CACHE_SIZE = 20;

/**
 * Wrappers that sit between the listbox and its options and must not add semantics of their own.
 */
const WRAPPER_SELECTOR =
  'ul, .splide, .splide__track, .splide__list, .splide__slide, slideshow-container, slideshow-slides';

/**
 * Finds the element that holds the options belonging to a section title.
 * @param {Element} title - The section title element
 * @returns {Element | null}
 */
function findOptionContainer(title) {
  const hasOptions = (element) =>
    element.querySelector('[ref="resultsItems[]"], .predictive-search-results__card') != null;

  for (let node = title; node; node = node.parentElement) {
    for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      if (hasOptions(sibling)) return sibling;
    }
  }

  return null;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  recentlySearchesKey = 'recently_searches';
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Markup already fetched for a search term, so backspacing or retyping a term does not refetch.
   * @type {Map<string, [string, string | null]>}
   */
  #resultsCache = new Map();

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * The header field driving the results as a dropdown
   * @type {HTMLInputElement | null}
   */
  #dropdownInput = null;

  /**
   * The input the user actually types in
   * @returns {HTMLInputElement}
   */
  get #activeInput() {
    return (this.#isDropdownOpen && this.#dropdownInput) || this.refs.searchInput;
  }

  get #isDropdownOpen() {
    return this.#dropdownInput != null && this.dialog?.refs.dialog?.hasAttribute('open') === true;
  }

  /**
   * Opens the results below the header search field, as a dropdown rather than a modal
   * @param {HTMLInputElement} input - The header search field.
   */
  openDropdown(input) {
    const dialog = this.dialog?.refs.dialog;

    if (!(input instanceof HTMLInputElement) || !dialog) return;

    const { predictiveSearchResults } = this.refs;

    this.#dropdownInput = input;
    input.setAttribute('aria-expanded', 'true');

    if (predictiveSearchResults.id) {
      input.setAttribute('aria-controls', predictiveSearchResults.id);
    }

    if (!dialog.hasAttribute('open')) {
      dialog.setAttribute('open', 'open');
    }

    document.addEventListener('pointerdown', this.#handleDropdownPointerDown, { capture: true });
    document.addEventListener('focusout', this.#handleDropdownFocusOut);
  }

  /**
   * Closes the dropdown without clearing the search term, leaving focus where it is
   */
  closeDropdown = () => {
    const input = this.#dropdownInput;

    if (!input) return;

    document.removeEventListener('pointerdown', this.#handleDropdownPointerDown, { capture: true });
    document.removeEventListener('focusout', this.#handleDropdownFocusOut);

    this.dialog?.refs.dialog?.removeAttribute('open');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');

    this.#clearCurrentItem();
  };

  /**
   * Whether the pointer went down inside the dropdown
   */
  #pointerInsideDropdown = false;

  /**
   * @param {Node | EventTarget | null} node
   */
  #isInsideDropdown(node) {
    if (!(node instanceof Node)) return false;

    const panel = this.dialog?.refs.dialog ?? this;

    return panel.contains(node) || this.#dropdownInput?.form?.contains(node) === true;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDropdownPointerDown = (event) => {
    this.#pointerInsideDropdown = this.#isInsideDropdown(event.target);

    if (!this.#pointerInsideDropdown) {
      this.closeDropdown();
      return;
    }

    document.addEventListener(
      'pointerup',
      () => {
        this.#pointerInsideDropdown = false;
      },
      { once: true, capture: true }
    );
  };

  /**
   * Closes the dropdown once focus leaves both the header field and the results
   * @param {FocusEvent} event
   */
  #handleDropdownFocusOut = (event) => {
    if (this.#pointerInsideDropdown) return;

    if (event.relatedTarget == null || this.#isInsideDropdown(event.relatedTarget)) return;

    this.closeDropdown();
  };

  connectedCallback() {
    super.connectedCallback();

    // Create the live region up front: a region added and filled in the same tick is missed.
    this.#ensureAnnouncer();
    this.#fillPendingEmptyProducts();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement) {
      this.#activeInput?.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * The section name last spoken, so it is not repeated for every option in that section.
   * @type {string}
   */
  #announcedGroup = '';

  /** @type {HTMLElement | null} */
  #announcer = null;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      item.setAttribute('role', 'option');
      item.id ||= `predictive-search-option-${itemIndex}`;

      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    const groups = this.#markListboxContainers();

    this.#announceGroup(activeItem, groups);
    this.#revealActiveItem(activeItem);

    const input = this.#activeInput;

    if (activeItem) {
      input.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    input.focus();
  }

  /**
   * Drops the list semantics of the wrappers between the results container and the options,
   * and turns every result section into a labelled group so its title is announced.
   */
  #markListboxContainers() {
    const { predictiveSearchResults } = this.refs;

    predictiveSearchResults.setAttribute('role', 'listbox');

    const groups = this.#labelOptionGroups();

    for (const wrapper of predictiveSearchResults.querySelectorAll(WRAPPER_SELECTOR)) {
      if (!groups.includes(wrapper)) wrapper.setAttribute('role', 'presentation');
    }

    this.#setOptionPositions(groups);

    return groups;
  }

  /**
   * Speaks the section name when the active option moves into a different section.
   * VoiceOver does not announce a group label on aria-activedescendant changes, so the name
   * has to come from a live region instead.
   * @param {HTMLElement | null} activeItem - The option that is currently active
   * @param {Element[]} groups - The labelled group elements
   */
  #announceGroup(activeItem, groups) {
    const group = activeItem ? groups.find((candidate) => candidate.contains(activeItem)) : null;
    const titleId = group?.getAttribute('aria-labelledby');
    const title = titleId ? document.getElementById(titleId) : null;
    const name = title?.textContent?.trim() ?? '';

    if (name === this.#announcedGroup) return;

    this.#announcedGroup = name;
    this.#ensureAnnouncer().textContent = name;
  }

  /**
   * Returns the live region used to announce section names, creating it on first use. It sits
   * outside the markup that gets replaced on every new search, so it survives re-renders.
   * @returns {HTMLElement}
   */
  #ensureAnnouncer() {
    if (this.#announcer?.isConnected) return this.#announcer;

    const announcer = document.createElement('span');

    announcer.className = 'visually-hidden';
    announcer.setAttribute('role', 'status');
    announcer.dataset.searchGroupAnnouncer = '';
    this.append(announcer);
    this.#announcer = announcer;

    return announcer;
  }

  /**
   * Turns each titled result section into a group labelled by its title, so screen readers
   * announce "Suggestions" or "Search Results" when entering it.
   * @returns {Element[]} The group elements, in document order
   */
  #labelOptionGroups() {
    const { predictiveSearchResults } = this.refs;
    const groups = [];

    for (const [index, title] of predictiveSearchResults
      .querySelectorAll('.predictive-search-results__title')
      .entries()) {
      const group = title.closest('slideshow-component') ?? findOptionContainer(title);

      if (!group) continue;

      title.id ||= `predictive-search-group-title-${index}`;
      group.setAttribute('role', 'group');
      group.setAttribute('aria-labelledby', title.id);
      groups.push(group);
    }

    return groups;
  }

  /**
   * Announces each option as "n of m" within its own section rather than across the whole dropdown.
   * @param {Element[]} groups - The labelled group elements
   */
  #setOptionPositions(groups) {
    for (const item of this.#allResultsItems ?? []) {
      const group = groups.find((candidate) => candidate.contains(item));
      const siblings = group ? (this.#allResultsItems ?? []).filter((other) => group.contains(other)) : [];

      if (!group || siblings.length < 2) {
        item.removeAttribute('aria-posinset');
        item.removeAttribute('aria-setsize');
        continue;
      }

      item.setAttribute('aria-posinset', `${siblings.indexOf(item) + 1}`);
      item.setAttribute('aria-setsize', `${siblings.length}`);
    }
  }

  /**
   * Brings the active option into view inside a Splide carousel, which moves by transform
   * and so cannot be reached with scrollIntoView.
   * @param {HTMLElement | null} item - The option that is currently active
   */
  #revealActiveItem(item) {
    const slide = item?.closest('.splide__slide');
    const slider = /** @type {HTMLElement & { splide?: { go: (index: number) => void } }} */ (
      slide?.closest('splide-slider')
    );

    if (!slide || !slider?.splide) return;

    const index = Array.from(slide.parentElement?.children ?? []).indexOf(slide);

    if (index >= 0) slider.splide.go(index);
  }

  /**
   * Drops the current selection without moving focus or touching the search term
   */
  #clearCurrentItem() {
    this.#announcedGroup = '';

    if (this.#announcer) this.#announcer.textContent = '';

    for (const item of this.#allResultsItems ?? []) {
      item.classList.remove('keyboard-focus');
      item.removeAttribute('aria-selected');
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.#isDropdownOpen) {
        this.closeDropdown();
      } else {
        this.#resetSearch();
      }
      return;
    }

    if (event.key === 'Tab' && this.#isDropdownOpen) {
      this.closeDropdown();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (this.#isDropdownOpen) {
          this.closeDropdown();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * Suggestions, collections, pages and articles come from predictive search, while products
   * come from the regular search so the modal matches the search results page.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) return;

    const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    predictiveUrl.searchParams.set('q', searchTerm);
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');

    const productsUrl = new URL(Theme.routes.search_url, location.origin);
    productsUrl.searchParams.set('q', searchTerm);
    productsUrl.searchParams.set('type', 'product');
    productsUrl.searchParams.set('options[prefix]', 'last');

    const cached = this.#resultsCache.get(searchTerm);

    if (cached) {
      // Cancel any slower in-flight request so it cannot overwrite these results.
      this.#activeFetch?.abort();
      this.#activeFetch = null;
      this.#renderSearchResults(searchTerm, cached[0], cached[1]);
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const [suggestionsMarkup, productsMarkup] = await Promise.all([
        sectionRenderer.getSectionHTML(sectionId, false, predictiveUrl),
        sectionRenderer.getSectionHTML(sectionId, false, productsUrl),
      ]);

      if (abortController.signal.aborted || !suggestionsMarkup) return;

      this.#cacheResults(searchTerm, suggestionsMarkup, productsMarkup);
      this.#renderSearchResults(searchTerm, suggestionsMarkup, productsMarkup);
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Predictive search failed', error);
    }
  }

  /**
   * Stores the markup for a search term, keeping the map bounded to the most recent terms.
   * @param {string} searchTerm
   * @param {string} suggestionsMarkup
   * @param {string | null} productsMarkup
   */
  #cacheResults(searchTerm, suggestionsMarkup, productsMarkup) {
    this.#resultsCache.delete(searchTerm);
    this.#resultsCache.set(searchTerm, [suggestionsMarkup, productsMarkup]);

    while (this.#resultsCache.size > SEARCH_CACHE_SIZE) {
      const oldest = this.#resultsCache.keys().next().value;
      if (oldest === undefined) break;
      this.#resultsCache.delete(oldest);
    }
  }

  /**
   * Writes fetched or cached markup into the results container.
   * @param {string} searchTerm
   * @param {string} suggestionsMarkup
   * @param {string | null} productsMarkup
   */
  #renderSearchResults(searchTerm, suggestionsMarkup, productsMarkup) {
    const { predictiveSearchResults } = this.refs;

    const predictiveSearchResultsEl = predictiveSearchResults.querySelector('#predictive-search-results');
    const predictiveSearchResultsUpdated = new DOMParser().parseFromString(suggestionsMarkup, 'text/html').getElementById('predictive-search-results');

    if (!predictiveSearchResultsEl || !predictiveSearchResultsUpdated) return;

    const productsList = productsMarkup
      ? new DOMParser().parseFromString(productsMarkup, 'text/html').getElementById('predictive-search-products')
      : null;

    if (productsList) {
      const inner = predictiveSearchResultsUpdated.querySelector('.predictive-search-results__inner');

      inner?.classList.remove('predictive-search-results__inner--no-results');
      inner?.querySelector('.predictive-search-results__no-results')?.remove();
      predictiveSearchResultsUpdated.querySelector('[data-single-result-url]')?.remove();
      inner?.append(productsList);
    }

    predictiveSearchResultsEl.innerHTML = predictiveSearchResultsUpdated.innerHTML;

    const slider = predictiveSearchResults.querySelector('splide-slider');

    if (slider) {
      document.dispatchEvent(new CustomEvent('splide:init', { detail: { slider } }));
    }

    this.#addRecentTerms(searchTerm);
    this.#resetScrollPositions();
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = [];
    //const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * The modal's empty state ships without the trending products (five product cards on every page);
   * fetch them from the predictive-search-empty section once this module runs.
   */
  async #fillPendingEmptyProducts() {
    const placeholder = this.querySelector('[data-empty-products-pending]');

    if (!placeholder) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');

      const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      const products = new DOMParser().parseFromString(markup, 'text/html').getElementById('predictive-search-products');

      if (!products || !placeholder.isConnected) return;

      placeholder.replaceWith(products);
      this.updatedCallback();
    } catch (error) {
      console.error('Trending products for the search modal did not load', error);
    }
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    if (this.#dropdownInput) this.#dropdownInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = [];
    //const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    const parsedEmptySectionMarkupWithRecentTerms = this.#buildRecentTerms(parsedEmptySectionMarkup);
    const predictiveSearchResultsEl = predictiveSearchResults.querySelector('#predictive-search-results');
    const predictiveSearchResultsUpdated = parsedEmptySectionMarkupWithRecentTerms.querySelector('#predictive-search-results');

    if (predictiveSearchResultsEl && predictiveSearchResultsUpdated) {
      predictiveSearchResultsEl.innerHTML = predictiveSearchResultsUpdated.innerHTML;
    }

    const slider = predictiveSearchResults.querySelector('splide-slider');

    if (slider) {
      document.dispatchEvent(new CustomEvent('splide:init', { detail: { slider } }));
    }

    this.#resetScrollPositions();
  };

  /**
   * 
   * @param {string} searchTerm 
   */
  #addRecentTerms(searchTerm) {
    const savedTerms = JSON.parse(localStorage.getItem(this.recentlySearchesKey)) || [];

    const filteredTerms = savedTerms.filter(term => term !== searchTerm);

    filteredTerms.push(searchTerm);

    const updatedTerms = filteredTerms.slice(-5);

    localStorage.setItem(this.recentlySearchesKey, JSON.stringify(updatedTerms));
  }

  /**
   *
   * @param {Element} emptyMarkup
   */
  #buildRecentTerms(emptyMarkup) {
    const terms = JSON.parse(localStorage.getItem(this.recentlySearchesKey)) || [];

    if (!terms.length) return emptyMarkup;

    let termsHtml = '';
    const fragment = document.createDocumentFragment();
    const recentTermsList = document.createElement('ul');
    const recentTermsTitle = document.createElement('div');

    recentTermsTitle.classList = 'predictive-search-results__title mx-2.5';
    recentTermsTitle.innerText = 'Recent Searches';

    recentTermsList.classList = 'predictive-search-results__list predictive-search-results__wrapper predictive-search-results__collections predictive-search-results__collections--row list-unstyled';

    terms.forEach(term => {
      const q = term.replace(' ', '+').replace('_', '+').replace('-', '+');

      termsHtml += `
        <li class="predictive-search-results__card--collection" ref="resultsItems[]" on:keydown="/onSearchKeyDown">
          <a href="/search?q=${ q }" class="pills__pill predictive-search-results__pill predictive-search-results__pill--collection">
            <span>${ term }</span>
          </a>
        </li>
      `;
    });

    recentTermsList.innerHTML = termsHtml;

    fragment.append(recentTermsTitle);
    fragment.append(recentTermsList);

    const searchPopular = emptyMarkup.querySelector('.search-popular-terms');

    if (searchPopular) {
      searchPopular.append(fragment);
    } else {
      emptyMarkup.querySelector('.predictive-search-results__inner')?.prepend(fragment);
    }

    return emptyMarkup;
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
